import React from "react";
import { Box, Divider, Typography, Stack } from "@mui/material";

const CocoonDetails = ({ order }) => {
  // Early return for missing cocoon specifics
  if (!order?.cocoonSpecifics) {
    return (
      <Box
        sx={{
          padding: 2,
          backgroundColor: "#f7f7f7",
          borderRadius: "8px",
          margin: "20px",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No Cocoon Details Available
        </Typography>
      </Box>
    );
  }

  const {
    fc1_cocoons_kg = 0,
    fc1_melted_cocoons_kg = 0,
    fc1_pupation_percent = 0,
    fc1_total_cocoons_kg = 0,
    fc2_cocoons_kg = 0,
    fc2_melted_cocoons_kg = 0,
    fc2_pupation_percent = 0,
    fc2_total_cocoons_kg = 0,
    fitForSeed = 0,
    fc2FitForSeed = 0,
    fc1_lot_no = "N/A",
    fc2_lot_no = "N/A",
    price = 0,
    date_cocoon_spinning = "N/A",
    date_cocoon_purchase = "N/A",
    total_cocoon_paid = 0,
    cocoon_payment_status = "PENDING",
    status = "N/A",
  } = order.cocoonSpecifics;

  // Calculations with safe defaults
  const totalFitForSeed = fitForSeed + fc2FitForSeed;
  const totalPayable = totalFitForSeed * price;
  const balanceAmount = totalPayable - total_cocoon_paid;

  // Grouped details for better organization
  const detailGroups = [
    {
      title: "FC1 Details",
      items: [
        { label: "Cocoons (kg)", value: fc1_cocoons_kg },
        { label: "Melted Cocoons (kg)", value: fc1_melted_cocoons_kg },
        { label: "Pupation (%)", value: fc1_pupation_percent },
        { label: "Total Cocoons (kg)", value: fc1_total_cocoons_kg },
        { label: "Lot Number", value: fc1_lot_no },
      ],
    },
    {
      title: "FC2 Details",
      items: [
        { label: "Cocoons (kg)", value: fc2_cocoons_kg },
        { label: "Melted Cocoons (kg)", value: fc2_melted_cocoons_kg },
        { label: "Pupation (%)", value: fc2_pupation_percent },
        { label: "Total Cocoons (kg)", value: fc2_total_cocoons_kg },
        { label: "Lot Number", value: fc2_lot_no },
      ],
    },
    {
      title: "General Details",
      items: [
        { label: "FC1 Fit for Seed", value: fitForSeed },
        { label: "FC2 Fit for Seed", value: fc2FitForSeed },
        { label: "Total Fit for Seed", value: totalFitForSeed },
        { label: "Cocoon Price", value: price.toFixed(2) },
        { label: "Spinning Date", value: date_cocoon_spinning },
        { label: "Purchase Date", value: date_cocoon_purchase },
      ],
    },
    {
      title: "Payment Details",
      items: [
        { label: "Total Payable", value: totalPayable.toFixed(2) },
        { label: "Total Paid", value: total_cocoon_paid.toFixed(2) },
        { label: "Balance Amount", value: balanceAmount.toFixed(2) },
        { label: "Payment Status", value: cocoon_payment_status },
        { label: "Status", value: status },
      ],
    },
  ];

  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: "#fafafa",
        borderRadius: "12px",
        margin: "20px",
        boxShadow: 1,
      }}
      aria-label="Cocoon Details"
    >
      <Stack spacing={3}>
        {detailGroups.map((group, index) => (
          <Box key={group.title}>
            {index > 0 && <Divider sx={{ my: 2 }} />}
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#333", mb: 2 }}
            >
              {group.title}
            </Typography>
            <Stack spacing={1}>
              {group.items.map((item) => (
                <Detail
                  key={item.label}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const Detail = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.5,
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: "medium", color: "#333", flex: 1 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#555", fontWeight: "normal", flex: 1, textAlign: "right" }}
    >
      {value ?? "N/A"}
    </Typography>
  </Box>
);

export default CocoonDetails;
